import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { contactAPI, fileAPI, groupAPI, adminAPI } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Send,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Inbox,
  Zap,
  TrendingUp,
  UserCheck,
  Filter,
  RotateCcw,
  Trash2,
  CornerDownRight,
  Paperclip,
  ImagePlus,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import { formatRelativeTime } from "@/lib/utils";

const severityOptions = [
  { value: "low", label: "Faible", icon: Info, color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
  { value: "medium", label: "Moyenne", icon: AlertCircle, color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" },
  { value: "high", label: "Élevée", icon: AlertTriangle, color: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
  { value: "critical", label: "Critique", icon: AlertCircle, color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" },
];

const statusLabels = {
  open: { label: "Ouverte", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400", icon: Clock },
  in_progress: { label: "En cours", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400", icon: Loader2 },
  resolved: { label: "Résolue", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400", icon: CheckCircle },
  closed: { label: "Fermée", color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400", icon: CheckCircle },
};

// ===================== ADMIN / MANAGER DASHBOARD =====================
function ContactDashboard() {
  const [contacts, setContacts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({ status: "", severity: "", group_id: "" });
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);

  useEffect(() => {
    loadContacts();
    loadStaff();
    loadGroups();
  }, []);

  const loadContacts = async (filterOverride) => {
    setLoading(true);
    try {
      const f = filterOverride || filters;
      const params = {};
      if (f.status) params.status = f.status;
      if (f.severity) params.severity = f.severity;
      if (f.group_id) params.group_id = f.group_id;
      const res = await contactAPI.listAll(params);
      setContacts(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des demandes");
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const res = await adminAPI.listUsers();
      setStaff(res.data.filter(u => ['admin', 'trainer', 'manager'].includes(u.role)));
    } catch (error) {
      console.error("Failed to load staff:", error);
    }
  };

  const loadGroups = async () => {
    try {
      const res = await groupAPI.list();
      setGroups(res.data);
    } catch (error) {
      console.error("Failed to load groups:", error);
    }
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await contactAPI.updateStatus(contactId, newStatus);
      setContacts(prev => prev.map(c =>
        c.id === contactId ? { ...c, status: newStatus } : c
      ));
      toast.success("Statut mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAssign = async (contactId, userId) => {
    try {
      await contactAPI.assign(contactId, userId);
      const assignee = staff.find(u => u.id === userId);
      setContacts(prev => prev.map(c =>
        c.id === contactId ? {
          ...c,
          assigned_to: userId,
          assigned_name: assignee ? `${assignee.first_name} ${assignee.last_name}` : '',
          status: 'in_progress'
        } : c
      ));
      toast.success("Demande assignée");
    } catch (error) {
      toast.error("Erreur lors de l'assignation");
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value === "all" ? "" : value };
    setFilters(newFilters);
    loadContacts(newFilters);
  };

  const resetFilters = () => {
    setFilters({ status: "", severity: "", group_id: "" });
    loadContacts({ status: "", severity: "", group_id: "" });
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) return;
    try {
      await contactAPI.delete(contactId);
      setContacts(prev => prev.filter(c => c.id !== contactId));
      setSelectedId(null);
      toast.success("Demande supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleReply = async (contactId) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const res = await contactAPI.reply(contactId, replyText.trim(), replyAttachments.map(f => f.url));
      // Update the contact in local state
      setContacts(prev => prev.map(c => {
        if (c.id === contactId) {
          const replies = c.replies || [];
          return { ...c, replies: [...replies, res.data.reply] };
        }
        return c;
      }));
      setReplyText("");
      setReplyingTo(null);
      setReplyAttachments([]);
      toast.success("Réponse envoyée et notification email envoyée au candidat");
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la réponse");
    } finally {
      setSendingReply(false);
    }
  };

  // Stats
  const totalOpen = contacts.filter(c => c.status === 'open').length;
  const totalInProgress = contacts.filter(c => c.status === 'in_progress').length;
  const totalCritical = contacts.filter(c => c.severity === 'critical' && c.status !== 'closed' && c.status !== 'resolved').length;
  const totalResolved = contacts.filter(c => c.status === 'resolved' || c.status === 'closed').length;

  const roleLabels = { admin: "Admin", trainer: "Formateur", manager: "Gestionnaire" };

  return (
    <>
      {/* CSS for blinking critical effect */}
      <style>{`
        @keyframes critical-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          50% { opacity: 0.85; box-shadow: 0 0 20px 4px rgba(239, 68, 68, 0.3); }
        }
        @keyframes critical-badge-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .critical-card {
          animation: critical-pulse 2s ease-in-out infinite;
          border-color: rgb(239 68 68 / 0.5) !important;
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(239, 68, 68, 0.02)) !important;
        }
        .critical-card:hover {
          border-color: rgb(239 68 68 / 0.7) !important;
        }
        .critical-badge-blink {
          animation: critical-badge-blink 1.2s ease-in-out infinite;
        }
        .dark .critical-card {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(239, 68, 68, 0.04)) !important;
        }
      `}</style>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card className="border-blue-200 dark:border-blue-800/50 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-background">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/10">
              <Inbox className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{totalOpen}</p>
              <p className="text-xs text-blue-600/70 dark:text-blue-400/70 font-medium">Ouvertes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 dark:border-yellow-800/50 bg-gradient-to-br from-yellow-50 to-white dark:from-yellow-950/30 dark:to-background">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-yellow-500/10">
              <TrendingUp className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{totalInProgress}</p>
              <p className="text-xs text-yellow-600/70 dark:text-yellow-400/70 font-medium">En cours</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-red-200 dark:border-red-800/50 bg-gradient-to-br from-red-50 to-white dark:from-red-950/30 dark:to-background ${totalCritical > 0 ? 'critical-card' : ''}`}>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500/10">
              <Zap className={`h-6 w-6 text-red-600 dark:text-red-400 ${totalCritical > 0 ? 'critical-badge-blink' : ''}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{totalCritical}</p>
              <p className="text-xs text-red-600/70 dark:text-red-400/70 font-medium">Critiques</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 dark:border-green-800/50 bg-gradient-to-br from-green-50 to-white dark:from-green-950/30 dark:to-background">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-500/10">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{totalResolved}</p>
              <p className="text-xs text-green-600/70 dark:text-green-400/70 font-medium">Résolues</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={filters.status || "all"} onValueChange={(v) => handleFilterChange("status", v)}>
            <SelectTrigger className="w-44 h-9">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="open">Ouverte</SelectItem>
              <SelectItem value="in_progress">En cours</SelectItem>
              <SelectItem value="resolved">Résolue</SelectItem>
              <SelectItem value="closed">Fermée</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.severity || "all"} onValueChange={(v) => handleFilterChange("severity", v)}>
            <SelectTrigger className="w-44 h-9">
              <SelectValue placeholder="Sévérité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes sévérités</SelectItem>
              <SelectItem value="low">Faible</SelectItem>
              <SelectItem value="medium">Moyenne</SelectItem>
              <SelectItem value="high">Élevée</SelectItem>
              <SelectItem value="critical">Critique</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={resetFilters}>
            <RotateCcw className="h-3.5 w-3.5" />
            Réinitialiser
          </Button>
          <Select value={filters.group_id || "all"} onValueChange={(v) => handleFilterChange("group_id", v)}>
            <SelectTrigger className="w-44 h-9">
              <SelectValue placeholder="Groupe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les groupes</SelectItem>
              {groups.map(g => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-muted-foreground">
            {contacts.length} demande{contacts.length !== 1 ? 's' : ''}
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : contacts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg">Aucune demande</h3>
            <p className="text-sm text-muted-foreground mt-1">Aucune demande de contact ne correspond à vos filtres</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {contacts.map(c => {
            const isCritical = c.severity === 'critical' && c.status !== 'closed' && c.status !== 'resolved';
            const isExpanded = selectedId === c.id;

            const sevColors = {
              low: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800",
              medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800",
              high: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border-orange-200 dark:border-orange-800",
              critical: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800",
            };
            const sevLabels = { low: "Faible", medium: "Moyenne", high: "Élevée", critical: "Critique" };
            const statColors = {
              open: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
              in_progress: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
              resolved: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
              closed: "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-400",
            };
            const statLabels = { open: "Ouverte", in_progress: "En cours", resolved: "Résolue", closed: "Fermée" };

            return (
              <Card
                key={c.id}
                className={`transition-all duration-300 overflow-hidden ${isCritical ? 'critical-card' : ''} ${isExpanded ? 'ring-2 ring-primary/20' : 'hover:shadow-md'}`}
              >
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer"
                  onClick={() => setSelectedId(isExpanded ? null : c.id)}
                >
                  {/* Severity indicator bar */}
                  <div className={`w-1.5 self-stretch rounded-full shrink-0 ${
                    c.severity === 'critical' ? 'bg-red-500' :
                    c.severity === 'high' ? 'bg-orange-500' :
                    c.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                  } ${isCritical ? 'critical-badge-blink' : ''}`} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-semibold text-sm ${isCritical ? 'text-red-700 dark:text-red-300' : ''}`}>{c.subject}</p>
                      {isCritical && <Zap className="h-4 w-4 text-red-500 critical-badge-blink shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold border ${sevColors[c.severity]} ${isCritical ? 'critical-badge-blink' : ''}`}>
                        {sevLabels[c.severity]}
                      </span>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statColors[c.status]}`}>
                        {statLabels[c.status]}
                      </span>
                      <span className="text-xs text-muted-foreground">• {c.group_name}</span>
                    </div>
                  </div>

                  <div className="text-right shrink-0 space-y-1">
                    <p className="text-sm font-medium">{c.user_name}</p>
                    <p className="text-xs text-muted-foreground">{formatRelativeTime(c.created_at)}</p>
                    {c.assigned_name && (
                      <div className="flex items-center gap-1 justify-end">
                        <UserCheck className="h-3 w-3 text-green-600" />
                        <span className="text-[10px] text-green-600 font-medium">{c.assigned_name}</span>
                      </div>
                    )}
                  </div>

                  <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t mx-4 pt-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="rounded-lg bg-muted/50 p-3">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Étudiant</p>
                        <p className="text-sm font-medium">{c.user_name}</p>
                        <p className="text-xs text-muted-foreground">{c.user_email}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-3">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Groupe</p>
                        <p className="text-sm font-medium">{c.group_name || "—"}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-3">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Date de création</p>
                        <p className="text-sm font-medium">{formatRelativeTime(c.created_at)}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Message</p>
                      <div className={`rounded-lg p-4 text-sm whitespace-pre-wrap leading-relaxed ${isCritical ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/50' : 'bg-muted/50'}`}>
                        {c.message}
                        {c.attachments && c.attachments.length > 0 && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            {c.attachments.map((url, ai) => (
                              <a key={ai} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                                <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="h-24 w-auto max-w-xs object-cover rounded-lg border hover:opacity-90 transition-opacity" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 items-center pt-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs font-semibold text-muted-foreground">Statut</Label>
                        <Select value={c.status} onValueChange={(v) => handleStatusChange(c.id, v)}>
                          <SelectTrigger className="h-8 w-36 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Ouverte</SelectItem>
                            <SelectItem value="in_progress">En cours</SelectItem>
                            <SelectItem value="resolved">Résolue</SelectItem>
                            <SelectItem value="closed">Fermée</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center gap-2">
                        <Label className="text-xs font-semibold text-muted-foreground">Assigner à</Label>
                        <Select value={c.assigned_to || ""} onValueChange={(v) => handleAssign(c.id, v)}>
                          <SelectTrigger className="h-8 w-52 text-xs">
                            <SelectValue placeholder="Sélectionner un responsable..." />
                          </SelectTrigger>
                          <SelectContent>
                            {staff.map(s => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.first_name} {s.last_name} ({roleLabels[s.role] || s.role})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        variant="destructive"
                        size="sm"
                        className="gap-1.5 ml-auto"
                        onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Supprimer
                      </Button>
                    </div>

                    {/* Existing replies */}
                    {c.replies && c.replies.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Réponses</p>
                        {c.replies.map((r, idx) => (
                          <div key={r.id || idx} className="rounded-lg bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800/50 p-3">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">{r.user_name}</span>
                              <span className="text-[10px] text-muted-foreground">{r.created_at ? formatRelativeTime(r.created_at) : ''}</span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap leading-relaxed">{r.message}</p>
                            {r.attachments && r.attachments.length > 0 && (
                              <div className="flex gap-2 mt-2">
                                {r.attachments.map((url, ai) => (
                                  <a key={ai} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="h-20 w-20 object-cover rounded-lg border hover:opacity-90 transition-opacity" />
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply form */}
                    <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                        <CornerDownRight className="h-3.5 w-3.5" />
                        Répondre à cette demande
                      </p>
                      <Textarea
                        placeholder="Écrivez votre réponse..."
                        className="min-h-[80px] text-sm"
                        value={replyingTo === c.id ? replyText : ''}
                        onClick={(e) => { e.stopPropagation(); setReplyingTo(c.id); }}
                        onChange={(e) => { setReplyingTo(c.id); setReplyText(e.target.value); }}
                      />
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors" onClick={(e) => e.stopPropagation()}>
                          <Paperclip className="h-3.5 w-3.5" />
                          <span>Joindre une capture</span>
                          <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const fd = new FormData(); fd.append("file", file); fd.append("storage_type", "local");
                              const grp = groups.find(g => g.id === c.group_id);
                              const wsForUpload = grp?.workspace_id || c.group_id;
                              const res = await fileAPI.upload(wsForUpload, fd);
                              setReplyAttachments(prev => [...prev, { url: res.data.url, name: file.name }]);
                            } catch(err) { toast.error("Erreur upload"); }
                            e.target.value = '';
                          }} />
                        </label>
                        {replyAttachments.length > 0 && (
                          <div className="flex gap-1">
                            {replyAttachments.map((f, i) => (
                              <div key={i} className="relative group">
                                <img src={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} alt="" className="h-10 w-10 object-cover rounded border" />
                                <button onClick={(e) => { e.stopPropagation(); setReplyAttachments(prev => prev.filter((_,j) => j !== i)); }} className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-3 h-3 text-[8px] flex items-center justify-center">×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          className="gap-1.5"
                          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                          disabled={sendingReply || !replyText.trim() || replyingTo !== c.id}
                          onClick={(e) => { e.stopPropagation(); handleReply(c.id); }}
                        >
                          {sendingReply ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                          Envoyer la réponse
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}

// ===================== STUDENT CONTACT FORM =====================
function StudentContactForm() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [contactFiles, setContactFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [form, setForm] = useState({
    group_id: "",
    severity: "",
    subject: "",
    message: "",
  });

  const handleContactFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("storage_type", "local");
      const group = groups[0];
      const wsId = group?.workspace_id || groups.find(g => g.id === form.group_id)?.workspace_id;
      if (!wsId) { toast.error("Sélectionnez un groupe d'abord"); setUploadingFile(false); return; }
      const res = await fileAPI.upload(wsId, formData);
      setContactFiles(prev => [...prev, { url: res.data.url, name: file.name }]);
    } catch (err) {
      toast.error("Erreur upload");
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [groupsRes, contactsRes] = await Promise.all([
        groupAPI.list(),
        contactAPI.listMy(),
      ]);
      setGroups(groupsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.group_id || !form.severity || !form.subject.trim() || !form.message.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    if (form.subject.trim().length < 5) {
      toast.error("Le sujet doit contenir au moins 5 caractères");
      return;
    }

    if (form.message.trim().length < 10) {
      toast.error("Le message doit contenir au moins 10 caractères");
      return;
    }

    setSubmitting(true);
    try {
      await contactAPI.create({ ...form, attachments: contactFiles.map(f => f.url) });
      toast.success("Votre demande a été envoyée avec succès !");
      setForm({ group_id: "", severity: "", subject: "", message: "" });
      const res = await contactAPI.listMy();
      setContacts(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const opt = severityOptions.find(s => s.value === severity);
    if (!opt) return null;
    const Icon = opt.icon;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${opt.color}`}>
        <Icon className="h-3 w-3" />
        {opt.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const s = statusLabels[status];
    if (!s) return null;
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${s.color}`}>
        <Icon className="h-3 w-3" />
        {s.label}
      </span>
    );
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
      {/* Contact Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Nouvelle demande
          </CardTitle>
          <CardDescription>
            Remplissez le formulaire ci-dessous pour contacter l'administration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="group">Groupe <span className="text-red-500">*</span></Label>
              <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                <SelectTrigger id="group">
                  <SelectValue placeholder="Sélectionnez votre groupe" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="severity">Priorité <span className="text-red-500">*</span></Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger id="severity">
                  <SelectValue placeholder="Sélectionnez la priorité" />
                </SelectTrigger>
                <SelectContent>
                  {severityOptions.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <span className="flex items-center gap-2">
                        <s.icon className="h-4 w-4" />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject">Sujet <span className="text-red-500">*</span></Label>
              <Input
                id="subject"
                placeholder="Résumez votre demande..."
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                maxLength={150}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message <span className="text-red-500">*</span></Label>
              <Textarea
                id="message"
                placeholder="Décrivez votre demande en détail..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground text-right">{form.message.length}/2000</p>
            </div>
            <div className="space-y-2">
              <Label>Captures d'écran (optionnel)</Label>
              <div className="flex flex-wrap gap-2">
                {contactFiles.map((f, i) => (
                  <div key={i} className="relative group">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} alt={f.name} className="h-16 w-16 object-cover rounded-lg border" />
                    <button onClick={() => setContactFiles(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                  </div>
                ))}
                <label className="h-16 w-16 flex items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 cursor-pointer transition-colors">
                  <input type="file" accept="image/*" className="hidden" onChange={handleContactFileUpload} disabled={uploadingFile} />
                  {uploadingFile ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : <ImagePlus className="h-5 w-5 text-muted-foreground" />}
                </label>
              </div>
            </div>
            <Button type="submit" className="w-full gap-2" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Envoyer la demande
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* My Contact Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Mes demandes
            {contacts.length > 0 && (
              <Badge variant="secondary" className="ml-2">{contacts.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Historique de vos demandes de contact
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <MessageSquare className="h-10 w-10 text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Aucune demande envoyée</p>
              <p className="text-sm text-muted-foreground mt-1">Utilisez le formulaire pour envoyer votre première demande</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contacts.map(c => (
                <div
                  key={c.id}
                  className="rounded-lg border p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{c.subject}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {getSeverityBadge(c.severity)}
                        {getStatusBadge(c.status)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                      <span>{formatRelativeTime(c.created_at)}</span>
                      {expandedId === c.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>
                  {expandedId === c.id && (
                    <div className="mt-3 pt-3 border-t space-y-3">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{c.message}</p>
                      {c.attachments && c.attachments.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {c.attachments.map((url, ai) => (
                            <a key={ai} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="h-20 w-auto rounded-lg border hover:opacity-90 transition-opacity" />
                            </a>
                          ))}
                        </div>
                      )}
                      {c.assigned_name && (
                        <p className="text-xs text-muted-foreground">
                          Assigné à : <span className="font-medium">{c.assigned_name}</span>
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Groupe : <span className="font-medium">{c.group_name}</span>
                      </p>
                      {c.replies && c.replies.length > 0 && (
                        <div className="space-y-2 mt-2">
                          <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">💬 Réponses ({c.replies.length})</p>
                          {c.replies.map((r, idx) => (
                            <div key={r.id || idx} className="rounded-lg bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800/50 p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">{r.user_name}</span>
                                <span className="text-[10px] text-muted-foreground">{r.created_at ? formatRelativeTime(r.created_at) : ''}</span>
                              </div>
                              <p className="text-sm whitespace-pre-wrap leading-relaxed">{r.message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===================== MAIN CONTACT PAGE =====================
export default function ContactPage() {
  const { isAdmin, isManager, canManage } = useAuth();
  const showDashboard = isAdmin || isManager;

  return (
    <AppLayout>
      <div className="page-container animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {showDashboard ? "Demandes de contact" : "Contact"}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {showDashboard
                ? "Centralisez, filtrez et traitez les demandes des étudiants"
                : "Envoyez une demande à l'administration"
              }
            </p>
          </div>
        </div>

        {showDashboard ? <ContactDashboard /> : <StudentContactForm />}
      </div>
    </AppLayout>
  );
}
